import React, { useEffect, useState } from "react";
import  { useDispatch, useSelector } from "react-redux";
import { Blade, Button, ReduxControls, ProgressIndicator, ErrorBanner } from "rhombus-react";
import { reduxForm, Field, getFormValues, change } from 'redux-form';

import DuplicateSuffixField from './DuplicateSuffixField';

import useActiveCreative from "hooks/useActiveCreative";

import { required } from "widgets/forms/validators/validators";
import { creativeNameMaxLength, creativeNameAllowedChars, CREATIVE_NAME_MAX_LENGTH } from 'widgets/forms/validators/Creatives';
import { isDynamic, isSocialCreative } from 'utils/common';
import { closeModal } from 'reducers/Modal';
import { duplicateSingleCreative, duplicateCreativesBulk, loadCreatives } from 'reducers/Creatives';

import { UPLOAD_CREATIVE_FILE_TYPES } from "utils/constants";

const DUPLICATE_CREATIVES_FORM = "duplicate-creatives-form";
const DUPLICATE_CREATIVE_SUFFIX = "_copy";

import "./DuplicateCreatives.css";

export function defaultNewName(creative, suffix = DUPLICATE_CREATIVE_SUFFIX) {
	const fileTypeString = UPLOAD_CREATIVE_FILE_TYPES.split(',')
		.map(type => type.trim())
		.join('|');
	const regex = new RegExp(`${fileTypeString}$`);
	const creativeName = creative?.name?.replace(regex, '');
	if (creativeName) {
		return `${creativeName}${suffix}`;
	}
	return '';
}

function areAllDynamic(creatives) {
	return creatives.every(creative => !isSocialCreative(creative) && isDynamic(creative));
}

function ErrorBanners({ errorMessages, successMessages }) {
	if (!errorMessages.length && !successMessages.length) {
		return null;
	}
	return (
		<div>
			{!!errorMessages.length && <ErrorBanner bannerType="error" title="Error! Some Creatives not Duplicated" messages={errorMessages} />}
			{!!successMessages.length && <ErrorBanner bannerType="success" title="Creatives Successfully Duplicated" messages={successMessages} />}
		</div>
	);
}

function DuplicateCreatives(props){
	let dispatch = useDispatch()
		, { creatives = [], libraryId, invalid } = props
		, isSingle = creatives.length === 1
		, displayVersions = areAllDynamic(creatives)
		, modalTitle = 'Duplicate Creative'
		, { isUpdating, formValues } = useSelector(state => ({
			isUpdating: state.creatives.isUpdating,
			formValues: getFormValues(DUPLICATE_CREATIVES_FORM)(state),
		}))
		, [errorMessages, setErrorMessages] = useState([])
		, [successMessages, setSuccessMessages] = useState([])
		, { newCreativeName, shouldCopyVersions, suffix }	= formValues || {}
		, onClose = () => dispatch(closeModal())
		, afterSubmit = (response) => {
			if (response?.success) {
				dispatch(closeModal());
				return dispatch(loadCreatives(libraryId));
			}
		}
		, onSubmit = () => {
			if (isSingle) {
				return dispatch(duplicateSingleCreative({
					copyVersions: shouldCopyVersions,
					creativeId: creatives[0].id,
					libraryId,
					newCreativeName,
				})).then(afterSubmit);
			} else {
				let creativeData = creatives.map((creative, i) => ({
					id: creative.id,
					newName: formValues.creatives[i].newName,
				}));
				return dispatch(duplicateCreativesBulk({
					libraryId,
					creatives: creativeData,
					copyVersions: shouldCopyVersions,
				})).then((response) => {
					if (!response.errorMessages.length) {
						//only auto-close modal if all creatives were duplicated successfully
						return afterSubmit({ success: true });
					} else {
						//if there were some errors, show user which creatives succeeded and which failed
						setErrorMessages(response.errorMessages);
						setSuccessMessages(response.successMessages);
					}
				});
			}
		}
		, updateCreativeSuffixes = (suffixStr = DUPLICATE_CREATIVE_SUFFIX) => {
			creatives.forEach((creative, i) => {
				let newName = defaultNewName(creatives[i], suffixStr);
				dispatch(change(DUPLICATE_CREATIVES_FORM, `creatives[${i}].newName`, newName));
			});
		}
		, setSuffix = () => updateCreativeSuffixes(suffix)
		;

	useActiveCreative(creatives[0]?.id);

	//init default creative duplicate names
	useEffect(() => {
		if (isSingle) {
			let newName = defaultNewName(creatives[0]);
			dispatch(change(DUPLICATE_CREATIVES_FORM, 'newCreativeName', newName));
		} else {
			dispatch(change(DUPLICATE_CREATIVES_FORM, 'suffix', DUPLICATE_CREATIVE_SUFFIX));
			updateCreativeSuffixes();
		}
	}, []);

	if (creatives.length > 1) {
		modalTitle = `Duplicate Creatives - ${creatives.length} Creatives`;
	} else if (creatives.length === 1) {
		modalTitle = `Duplicate Creative - ${creatives[0].name}`;
	}

	const renderContentSingle = () => (
		<div className="duplicate-creative-content single-duplicate">
			<ProgressIndicator isLoading={isUpdating} />
			<Field
				name="newCreativeName"
				label="Name for Duplicated Creative"
				component={ReduxControls.Input}
				autoFocus
				required
				validate={[required, creativeNameMaxLength, creativeNameAllowedChars]}
				showCharCount
				charMax={CREATIVE_NAME_MAX_LENGTH}
			/>
			{displayVersions && (
				<Field
					name="shouldCopyVersions"
					label="Include Versions"
					component={ReduxControls.Checkbox}
					useValue
				/>
			)}
		</div>
	);

	const renderContentBulk = () => (
		<div className="duplicate-creative-content bulk-duplicate">
			<ProgressIndicator isLoading={isUpdating} />
			<ErrorBanners errorMessages={errorMessages} successMessages={successMessages} />
			<Field
				name="suffix"
				label="Append to All Duplicated Creative Names"
				component={DuplicateSuffixField}
				props={{ onUndo: setSuffix }}
				className="suffix-container"
			/>
			<hr />
			{creatives.map((creative, i) => (
				<Field
					key={creative.id}
					name={`creatives[${i}].newName`}
					label={creative.name}
					component={ReduxControls.Input}
					type="text"
					showCharCount
					charMax={CREATIVE_NAME_MAX_LENGTH}
					validate={[required, creativeNameMaxLength, creativeNameAllowedChars]}
				/>
			))}
			{displayVersions && (
				<Field
					name="shouldCopyVersions"
					label="Include Versions"
					component={ReduxControls.Checkbox}
					useValue
				/>
			)}
		</div>
	);

	const renderFooter = () => ([
		<Button key="cancel" theme="default" disabled={isUpdating} onClick={onClose}>Cancel</Button>,
		<Button
			key="submit"
			theme="green"
			disabled={isUpdating || invalid}
			onClick={onSubmit}
		>
			Duplicate
		</Button>
	]);

	return (
		<Blade
			type="modal"
			setAppTitle={false}
			className="duplicate-creatives"
			title={modalTitle}
			allowFullScreen={false}
			xClose
			bladeProps={{ verticallyCenter: true }}
			onClose={onClose}
			renderContent={creatives.length > 1 ? renderContentBulk : renderContentSingle}
			renderFooter={renderFooter}
		/>
	);
}

DuplicateCreatives.displayName = "DuplicateCreatives";

export default reduxForm({
	form: DUPLICATE_CREATIVES_FORM,
	initialValues: {
		suffix: DUPLICATE_CREATIVE_SUFFIX,
		creatives: [],
		newCreativeName: '',
		shouldCopyVersions: false,
	},
})(DuplicateCreatives);

